import { EndingCountdownExpression } from "lib/project_util";
import dynamic from "next/dynamic";
import React, { FC, HTMLAttributes, useMemo } from "react";
import PcCourseButtonBottom from "../../custom/PcCourseButtonBottom";
import { GoalAmountArea, Rating } from "../atoms";
import { EnclosurePc } from "components/front/layout/EnclosurePc";
import {
  OrderCountLabel,
  RemainingDaysLabel,
  RemainingDaysValueProps,
  StyledBigValue,
  StyledSmallValue,
  StyledSmallValue2,
  StyledValue,
  SupportAmountLabel,
} from "./BusinessComponents";
import styled from "styled-components";
import SsMainButton from "../../layout/SsMainButton";
import Link from "next/link";
import { ProjectTotalization } from "interfaces/front/business";

const RemainingDaysValue = dynamic<RemainingDaysValueProps>(() => import("./BusinessComponents").then((m) => m.RemainingDaysValue), {
  ssr: false,
});

export type Props = HTMLAttributes<HTMLDivElement> & {
  title: string;
  twitterHashtags: (string | null)[];
  supportAmount: string | undefined;
  supportAmountPool: string | undefined;
  rating?: number | null;
  goalAmount: string | undefined;
  summary: ProjectTotalization | undefined;
  remaining: EndingCountdownExpression | null;
  slug: string;
  isPreviewMode: boolean;
  isShopping: boolean;
  showsSupportAmount: boolean;
};
const QuantitativeSectionPc: FC<Props> = ({
  title,
  twitterHashtags,
  supportAmount,
  supportAmountPool,
  rating,
  goalAmount,
  summary,
  remaining,
  slug,
  isPreviewMode,
  showsSupportAmount,
  isShopping,
  ...props
}) => {
  const hasOrderCount = useMemo(() => typeof summary?.order_count === "number", [summary?.order_count]);
  const kamiari = (slug === "kamiari") ? "※この応援額は出雲市全体の寄附状況です。(随時更新します)" : ""
  return (
    <EnclosurePc {...props}>
      <InformationArea $shows={showsSupportAmount}>
        {(!isShopping &&
          <Row>
            <StyledSupportAmountLabel>現在の応援額</StyledSupportAmountLabel>
            <ValueArea1 $visible>
              <StyledBigValue>{supportAmount ?? "計算中"}</StyledBigValue>
              <StyledUnit $visible={!!supportAmount}>円</StyledUnit>
            </ValueArea1>
            <ValueArea1 $visible={!!supportAmountPool} $none={!!kamiari && !supportAmountPool}>
              (<span style={{ marginRight: "5px" }}>未入金</span>
              <StyledSmallValue>{supportAmountPool ?? "?"}</StyledSmallValue>
              <StyledUnit $visible={!!supportAmountPool}>円</StyledUnit>)
            </ValueArea1>
            <ValueArea1 $visible={!!kamiari}>
              <StyledSmallValue2 >{kamiari}</StyledSmallValue2>
            </ValueArea1>
            {rating !== null && <StyledRating rate={rating} />}
            {goalAmount && <GoalAmountArea amount={goalAmount} />}
          </Row>
        )}
        <FlexRow>
          <div>
            <OrderCountLabel>{isShopping ? "購入者数" : "応援者数"}</OrderCountLabel>
            <ValueArea2>
              <StyledValue>{summary?.order_count ?? "計算中"}</StyledValue>
              <StyledUnit $visible={hasOrderCount}>人</StyledUnit>
            </ValueArea2>
          </div>
          {slug != "kamiari" && (
            <div>
              <RemainingDaysLabel>残り日数</RemainingDaysLabel>
              <ValueArea2>
                <RemainingDaysValue remaining={remaining} />
              </ValueArea2>
            </div>
          )}
        </FlexRow>
      </InformationArea>
      <Link href={{ pathname: `${isPreviewMode ? "/admin/preview" : ""}/project/${slug}`, hash: "course" }} passHref>
        <StyledSsMainButton>{isShopping ? "購入する商品を選ぶ" : "プロジェクトを応援する"}</StyledSsMainButton>
      </Link>
      <PcCourseButtonBottom projectCode={slug} title={title} twitterHashtags={twitterHashtags} />
    </EnclosurePc>
  );
};
export default QuantitativeSectionPc;

const InformationArea = styled.div<{ $shows: boolean }>`
  display: ${({ $shows }) => ($shows ? "block" : "none")};
  margin-bottom: 30px;
  @media screen and (min-width: 1281px) {
    font-size: 16px;
  }
`;
const Row = styled.div`
  margin-bottom: 20px;
`;
const FlexRow = styled(Row)`
  display: flex;
  & > * {
    flex-basis: 50%;
  }
`;
const StyledSupportAmountLabel = styled(SupportAmountLabel)`
  margin-bottom: 14px;
`;
const ValueArea1 = styled.div<{ $visible: boolean, $none?: boolean }>`
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  display: ${({ $none = false }) => ($none ? "none" : "block")};
  margin: 0 0 10px 20px;
`;
const ValueArea2 = styled.p`
  margin: 13px 0 0 20px;
`;
const StyledSsMainButton = styled(SsMainButton)`
  margin: 20px 0;
  height: 60px;
  font-size: 24px;
  font-style: italic;
  color: #1a1a1a;
`;
const StyledUnit = styled.span<{ $visible: boolean }>`
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  margin-left: 5px;
  @media screen and (max-width: 960px) {
    font-size: 12px;
  }
  @media screen and (min-width: 961px) and (max-width: 1280px) {
    font-size: 14px;
  }
  @media screen and (min-width: 1281px) {
    font-size: 16px;
  }
`;
const StyledRating = styled(Rating)<{ rate?: number | null }>`
  visibility: ${({ rate }) => (rate !== undefined ? "visible" : "hidden")};
`;
